import { Clipboard } from '@capacitor/clipboard';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonSecondary from 'components/shared/ButtonSecondary';
import SVG from 'components/icons/SVG';
import FormError from 'components/shared/FormError';
import Field from 'components/shared/Field';
import Heading from 'components/shared/Heading';
import ButtonText from 'components/shared/ButtonText';
import Text from 'components/shared/Text';
import STEPS from 'constants/steps';
import {
  actionVerifyFirebaseMfaSMSCode,
  actionVerifyLogin,
  initFirebaseMfa,
  setStep
} from 'reduxConfig/auth.creators';
import { createLogger } from 'helpers/logger';
import styles from './styles.module.scss';

const log = createLogger('[auth] <VerifyCode>:');

const microapps = window.MICROFRONTENDS_CONFIG;
const SMS_CODE_TIMEOUT_MS = 20000;

function VerifyCode({
  dispatch,
  error,
  loading,
  step,
  isMobile,
  firebaseAuthMobileState,
  firebaseMfaVerificationId: verificationId,
  firebaseMfaResolver: resolver,
  firebaseMfaPhoneNumber: nativeResolverPhoneNumber
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [isSMSTimeout, setIsSMSTimeout] = useState(false);
  const [hasBeenResent, setHasBeenResent] = useState(false);
  const resendTimeout = useRef();

  const paste = useCallback(async () => {
    try {
      const { value } = await Clipboard.read();
      setCode(value);
    } catch {} // eslint-disable-line no-empty
  }, []);

  useEffect(() => {
    resendTimeout.current = setTimeout(() => setIsSMSTimeout(true), SMS_CODE_TIMEOUT_MS);
    return () => {
      setUseRecoveryCode(false);
      resendTimeout.current && clearTimeout(resendTimeout.current);
    };
  }, []);

  const handleResendSMSCode = useCallback(() => {
    log.info('handleResendSMSCode, resolver?.hints[0]?.factorId', resolver?.hints[0]?.factorId);
    dispatch(initFirebaseMfa({ resolver, recaptchaContainerId: 'recaptcha-verifier' }));
    resendTimeout.current && clearTimeout(resendTimeout.current);
    setHasBeenResent(true);
    resendTimeout.current = setTimeout(() => {
      setHasBeenResent(false);
      setIsSMSTimeout(true);
    }, SMS_CODE_TIMEOUT_MS);
  }, [resolver, dispatch]);

  const toggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode);
  };

  const handleChange = e => setCode(e.target.value);

  const handleSubmit = e => {
    e.preventDefault();
    if (code) {
      if (step === STEPS.verifySMSCode) {
        log.info('dispatch actionVerifyFirebaseMfaSMSCode', code);
        dispatch(actionVerifyFirebaseMfaSMSCode({ resolver, verificationId, verificationCode: code }));
      } else {
        dispatch(
          actionVerifyLogin({
            email: firebaseAuthMobileState?.email,
            idToken: firebaseAuthMobileState?.idToken,
            twoFactorCode: code,
            useRecoveryCode
          })
        );
      }
      setIsSMSTimeout(false);
      setHasBeenResent(false);
    }
  };

  useEffect(() => {
    if (step === STEPS.verifyCode) setCode('');
  }, [step]);

  if (step !== STEPS.verifyCode && step !== STEPS.verifySMSCode)
    return <Redirect to={microapps.routes.login.path} />;

  return (
    <>
      <SEO title={t('auth_2fa_meta_title')} />
      <Heading tag="h1">{t('auth_2fa_title')}</Heading>
      <span id="recaptcha-verifier"></span>

      {step === STEPS.verifySMSCode ? (
        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {t('auth_2fa_prompt_code_sms_login', {
            phone: resolver?.hints[0]?.phoneNumber || nativeResolverPhoneNumber || ''
          })}
        </Text>
      ) : (
        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {useRecoveryCode ? t('auth_2fa_prompt_recovery') : t('auth_2fa_prompt_code')}
        </Text>
      )}

      <form className={styles.form} onSubmit={handleSubmit} autoComplete="on">
        <Field
          autoFocus
          autoComplete="one-time-code"
          id="code"
          type={useRecoveryCode ? 'text' : 'tel'}
          pattern={!useRecoveryCode && '[0-9 ]*'}
          maxLength={useRecoveryCode ? 100 : 6}
          name={
            step === STEPS.verifySMSCode
              ? t('common_sms_code')
              : useRecoveryCode
              ? t('common_recovery_code')
              : t('common_2fa')
          }
          placeholder={t('common_code')}
          onChange={handleChange}
          value={code}
          ignoreMarginBottom
          size={useRecoveryCode ? 'medium' : 'small'}
          disabled={loading}
        />

        <div className={styles.verifyButtonWrap}>
          <ButtonPrimary
            disabled={!code || loading}
            loading={loading}
            styles={[styles.verifyButton, { [styles.verifyButtonRecoveryCode]: useRecoveryCode }]}
            type="submit">
            {t('common_verify')}
          </ButtonPrimary>
        </div>
      </form>

      <ButtonSecondary styles={[styles.pasteButton]} onClick={paste} disabled={loading}>
        <SVG icon="paste" fill="var(--text)" height="14" width="14" /> {t('auth_login_paste')}
      </ButtonSecondary>

      {error && <FormError>{t(error)}</FormError>}

      {step === STEPS.verifyCode && (
        <>
          {isMobile ? (
            <Text muted tall styles={[styles.toggleMobile]}>
              {useRecoveryCode ? t('auth_2fa_lost_recovery') : t('auth_2fa_lost_code')}
              <ButtonText onClick={() => toggleRecoveryCode()} underlined muted>
                {useRecoveryCode ? t('auth_2fa_use_code') : t('auth_2fa_use_recovery')}
              </ButtonText>
            </Text>
          ) : (
            <ButtonText onClick={() => toggleRecoveryCode()} underlined muted>
              {useRecoveryCode ? t('auth_2fa_use_code') : t('auth_2fa_use_recovery')}
            </ButtonText>
          )}
        </>
      )}

      {step === STEPS.verifySMSCode && isSMSTimeout && (
        <>
          <Text tag="p" muted>
            {hasBeenResent ? t('auth_2fa_resend_sms_success') : t('auth_2fa_resend_sms_prompt')}
          </Text>
          <ButtonSecondary onClick={handleResendSMSCode} disabled={loading || hasBeenResent}>
            {t('auth_2fa_resend_sms')}
          </ButtonSecondary>
        </>
      )}

      <div className={styles.cancelButtonWrap}>
        <ButtonText onClick={() => dispatch(setStep(STEPS.RESET))}>{t('common_cancel')}</ButtonText>
      </div>
    </>
  );
}

VerifyCode.propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  firebaseAuthMobileState: PropTypes.object
};

VerifyCode.defaultProps = {
  error: null,
  step: null,
  firebaseAuthMobileState: null
};

export default VerifyCode;
